import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { database, useAuth, storage } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";

import { get, set, push, ref } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

import { Heart, MessageCircle, ExternalLink, Github, Calendar, Users } from "lucide-react";
import { format } from "date-fns";

export default function ProjectDetailsPage() {
  const { id } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // project & UI state
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  // taken by / progress state
  const [takenList, setTakenList] = useState([]);
  const [hasTaken, setHasTaken] = useState(false);

  // progress form state
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [progressFile, setProgressFile] = useState(null);
  const [progressCommitLink, setProgressCommitLink] = useState("");

  // progress updates list
  const [progressUpdates, setProgressUpdates] = useState([]);

  useEffect(() => {
    fetchProject();
    fetchComments();
    fetchTakenUsers();
    fetchProgressUpdates();

  }, [id]);

  // -----------------------
  // FETCH PROJECT
  // -----------------------
  const fetchProject = async () => {
    try {
      const snapshot = await get(ref(database, `projects/${id}`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        setProject(data);
        // Set like status correctly
        const likedByUser = data.likes && data.likes[userProfile?.uid];
        setLiked(!!likedByUser);
      } else {
        setProject(null);
      }
    } catch (error) {
      toast.error("Failed to load project");
      console.error("fetchProject:", error);
    }
    setLoading(false);
  };

  // -----------------------
  // FETCH COMMENTS
  // -----------------------
  const fetchComments = async () => {
    try {
      const snapshot = await get(ref(database, `projects/${id}/comments`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([key, value]) => ({
          id: key,
          ...value,
        }));
        setComments(list);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("fetchComments:", error);
    }
  };

  // -----------------------
  // FETCH TAKEN USERS
  // -----------------------
  const fetchTakenUsers = async () => {
    try {
      const snapshot = await get(ref(database, `projects/${id}/taken_by`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.entries(data).map(([key, value]) => ({
          id: key,
          ...value,
        }));
        setTakenList(list);
        setHasTaken(!!data[userProfile?.uid]);
      } else {
        setTakenList([]);
        setHasTaken(false);
      }
    } catch (error) {
      console.error("fetchTakenUsers:", error);
    }
  };

  // -----------------------
  // FETCH PROGRESS UPDATES
  // -----------------------
  const fetchProgressUpdates = async () => {
    try {
      const snapshot = await get(ref(database, `projects/${id}/progress_updates`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        // convert to array and sort by created_at desc
        const list = Object.entries(data)
          .map(([key, value]) => ({ id: key, ...value }))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setProgressUpdates(list);
      } else {
        setProgressUpdates([]);
      }
    } catch (error) {
      console.error("fetchProgressUpdates:", error);
    }
  };

  // -----------------------
  // HANDLE TAKE PROJECT
  // -----------------------
  const handleTakeProject = async () => {
    if (!project) {
      toast.error("Project data not loaded");
      return;
    }
    if (project.uid === userProfile.uid) {
      toast.error("You cannot take your own project.");
      return;
    }
    if (hasTaken) {
      toast.error("You have already taken this project.");
      return;
    }

    try {
      const userData = {
        full_name: userProfile.full_name,
        email: userProfile.email,
        uid: userProfile.uid,
        taken_at: new Date().toISOString(),
      };

      await set(ref(database, `projects/${id}/taken_by/${userProfile.uid}`), userData);

      toast.success("You have taken this project!");
      setHasTaken(true);
      fetchTakenUsers();
    } catch (error) {
      console.error("handleTakeProject:", error);
      toast.error("Failed to take project.");
    }
  };

  // -----------------------
  // HANDLE LIKE
  // -----------------------
  const handleLike = async () => {
    try {
      const likesRef = ref(database, `projects/${id}/likes`);
      const snapshot = await get(likesRef);
      let likes = snapshot.exists() ? snapshot.val() : {};

      if (liked) {
        delete likes[userProfile.uid];
      } else {
        likes[userProfile.uid] = true;
      }

      await set(likesRef, likes);
      setLiked(!liked);
      fetchProject();
    } catch (error) {
      toast.error("Failed to like project");
      console.error("handleLike:", error);
    }
  };

  // -----------------------
  // ADD COMMENT
  // -----------------------
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const commentRef = ref(database, `projects/${id}/comments`);
      await push(commentRef, {
        text: newComment,
        created_at: new Date().toISOString(),
        user_name: userProfile.full_name,
        uid: userProfile.uid,
      });
      setNewComment("");
      fetchComments();
      toast.success("Comment added");
    } catch (error) {
      console.error("handleCommentSubmit:", error);
      toast.error("Failed to add comment");
    }
  };

  // -----------------------
  // PROGRESS FILE CHANGE
  // -----------------------
  const handleProgressFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    if (selectedFile.size > 5 * 1024 * 1024) {
      // allow bigger file for progress (5MB)
      toast.error("File size should be less than 5MB");
      return;
    }
    setProgressFile(selectedFile);
  };

  // -----------------------
  // SUBMIT PROGRESS UPDATE
  // -----------------------
  const handleAddProgressSubmit = async (e) => {
    e.preventDefault();

    if (!hasTaken) {
      toast.error("You must take the project before posting progress.");
      return;
    }

    if (!progressMessage.trim() && !progressFile && !progressCommitLink) {
      toast.error("Please add a message, file, or commit link.");
      return;
    }

    try {
      // create new update id
      const updatesRef = ref(database, `projects/${id}/progress_updates`);
      const newUpdateRef = push(updatesRef);
      const updateId = newUpdateRef.key;

      // base update data
      const updateData = {
        userId: userProfile.uid,
        user_name: userProfile.full_name,
        progress: Number(progressPercent) || 0,
        message: progressMessage || "",
        commit_url: progressCommitLink || "",
        created_at: new Date().toISOString(),
        file_url: null,
        file_path: null,
      };

      // upload file if exists
      if (progressFile) {
        const filePath = `project_progress/${id}/${updateId}/${progressFile.name}`;
        const fileRef = storageRef(storage, filePath);
        await uploadBytes(fileRef, progressFile);
        const downloadURL = await getDownloadURL(fileRef);
        updateData.file_url = downloadURL;
        updateData.file_path = filePath;
      }

      // save update
      await set(newUpdateRef, updateData);

      // update a quick summary field at project level (latest_progress)
      await set(
        ref(database, `projects/${id}/latest_progress/${userProfile.uid}`),
        {
          user_name: userProfile.full_name,
          progress: updateData.progress,
          updated_at: updateData.created_at,
        }
      );

      // refresh local lists & reset form
      setProgressMessage("");
      setProgressCommitLink("");
      setProgressFile(null);
      setProgressPercent(0);
      setShowProgressForm(false);

      fetchProgressUpdates();
      fetchProject(); // refresh project (if you display aggregated fields)
      toast.success("Progress update added");
    } catch (error) {
      console.error("handleAddProgressSubmit:", error);
      toast.error("Failed to add progress update");
    }
  };

  // -----------------------
  // Calculate overall progress (simple average of latest_progress entries)
  // -----------------------
  const computeOverallProgress = () => {
    if (!project) return 0;
    // prefer latest_progress node if it exists
    if (project.latest_progress) {
      const arr = Object.values(project.latest_progress);
      if (arr.length === 0) return 0;
      const sum = arr.reduce((acc, cur) => acc + Number(cur.progress || 0), 0);
      return Math.round(sum / arr.length);
    }

    // fallback to compute from progressUpdates: gather latest update per user
    const latestMap = {};
    progressUpdates.forEach((u) => {
      if (!latestMap[u.userId]) {
        latestMap[u.userId] = u.progress || 0;
      }
    });
    const counts = Object.keys(latestMap).length;
    if (counts === 0) return 0;
    const sum = Object.values(latestMap).reduce((a, b) => a + Number(b), 0);
    return Math.round(sum / counts);
  };

  // -----------------------
  // LOADING SCREEN
  // -----------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="glass p-8 rounded-2xl skeleton h-96"></div>
        </div>
      </div>
    );
  }

  // -----------------------
  // NOT FOUND
  // -----------------------
  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Project not found</p>
        </div>
      </div>
    );
  }

  // overall progress
  const overallProgress = computeOverallProgress();

  // -----------------------
  // PAGE CONTENT
  // -----------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* BACK BUTTON */}
        <Button variant="ghost" onClick={() => navigate("/feed")} className="mb-4">
          ← Back to Feed
        </Button>

        {/* PROJECT CARD */}
        <Card className="glass border-white/50 mb-6">
          <CardHeader className="relative">
            {/* Edit button for owner */}
            {project.uid === userProfile.uid && (
              <Button
                variant="outline"
                className="absolute right-0 top-0"
                onClick={() => navigate(`/edit-project/${id}`)}
              >
                Edit Project
              </Button>
            )}

            {/* Take Project button for non-owners */}
            {project.uid !== userProfile.uid && (
              <Button
                variant="outline"
                className="absolute right-32 top-0 bg-green-600 text-white hover:bg-green-700"
                onClick={handleTakeProject}
                disabled={hasTaken}
              >
                {hasTaken ? "Project Taken" : "Take This Project"}
              </Button>
            )}

            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Avatar + Name */}
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-gradient-to-br from-sky-400 to-cyan-500 text-white">
                      {project.user_name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{project.user_name}</p>
                    <p className="text-sm text-gray-500">{project.college_name}</p>
                  </div>
                </div>

                {/* Title */}
                <CardTitle className="text-3xl mb-2">{project.title}</CardTitle>

                {/* Date + Status */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(project.created_at), "MMM dd, yyyy")}
                  </div>

                  <Badge
                    className={
                      project.status === "Completed"
                        ? "bg-green-100 text-green-800"
                        : project.status === "In Development"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-amber-100 text-amber-800"
                    }
                  >
                    {project.status}
                  </Badge>

                  {/* overall progress */}
                  <div className="ml-4 text-sm">
                    <span className="font-semibold">Progress: </span>
                    <span>{overallProgress}%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Tech Stack */}
            <div>
              <h3 className="font-semibold mb-3">Tech Stack</h3>
              <div className="flex flex-wrap gap-2">
                {(project.tech_stack || []).map((tech) => (
                  <Badge key={tech} variant="secondary">
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            {/* Description */}
            <div>
              <h3 className="font-semibold mb-3">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap break-words leading-relaxed max-w-full overflow-x-auto">
                {project.description}
              </p>
            </div>

            <Separator />

            {/* GitHub Link */}
            {project.github_link && (
              <div>
                <h3 className="font-semibold mb-3">Repository</h3>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => window.open(project.github_link, "_blank")}
                >
                  <Github className="w-4 h-4" />
                  View on GitHub
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Attachment */}
            {project.file_url && (
              <>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-3">Attachment</h3>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => window.open(project.file_url, "_blank")}
                  >
                    Download File
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}

            {/* Collaboration */}
            {project.allow_collaboration && (
              <>
                <Separator />
                <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                  <div className="flex items-center gap-2 text-sky-700">
                    <Users className="w-5 h-5" />
                    <span className="font-semibold">Open for Collaboration</span>
                  </div>
                  <p className="text-sm text-sky-600 mt-2">The project owner is looking for collaborators.</p>
                </div>
              </>
            )}

            <Separator />

            {/* Action buttons (Like + Add progress if allowed) */}
            <div className="flex gap-4 items-center">
              <Button
                variant={liked ? "default" : "outline"}
                className={`gap-2 ${liked ? "bg-gradient-to-r from-sky-500 to-cyan-500" : ""}`}
                onClick={handleLike}
              >
                <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
                {project.likes ? Object.keys(project.likes).length : 0}
              </Button>

              {/* Show "Add Progress" only if user has taken the project */}
              {hasTaken && (
                <Button
                  variant="secondary"
                  onClick={() => setShowProgressForm((s) => !s)}
                  className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white"
                >
                  {showProgressForm ? "Close Progress Form" : "Add Progress Update"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* PROGRESS FORM (visible only when hasTaken && toggled) */}
        {hasTaken && showProgressForm && (
          <Card className="glass border-white/50 mb-6">
            <CardHeader>
              <CardTitle>Add Progress Update</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddProgressSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Progress %</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progressPercent}
                    onChange={(e) => setProgressPercent(e.target.value)}
                    className="w-full"
                  />
                  <div className="text-sm mt-1">{progressPercent}%</div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Update Message</label>
                  <textarea
                    rows={4}
                    className="w-full rounded-md border p-2"
                    value={progressMessage}
                    onChange={(e) => setProgressMessage(e.target.value)}
                    placeholder="What did you finish? What is pending?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">GitHub Commit / Demo Link (optional)</label>
                  <Input
                    placeholder="https://github.com/your-repo/commit/..."
                    value={progressCommitLink}
                    onChange={(e) => setProgressCommitLink(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Attach File (optional, max 5MB)</label>
                  <Input type="file" accept=".zip,.pdf,.png,.jpg,.txt,.docx" onChange={handleProgressFileChange} />
                  {progressFile && <div className="text-sm mt-1">{progressFile.name}</div>}
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white">
                    Post Update
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowProgressForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* LIST OF PEOPLE WHO TOOK THE PROJECT */}
        {takenList.length > 0 && (
          <Card className="glass border-white/50 mb-6">
            <CardHeader>
              <CardTitle>Developers Who Took This Project</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {takenList.map((dev) => (
                  <div key={dev.id} className="flex items-center gap-3 p-3 bg-white/60 rounded-lg">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white">
                        {dev.full_name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold">{dev.full_name}</p>
                      <p className="text-xs text-gray-500">{dev.email}</p>
                      <p className="text-xs text-gray-500">Taken on: {format(new Date(dev.taken_at), "MMM dd, yyyy")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* PROGRESS TIMELINE */}
        <Card className="glass border-white/50 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Progress Updates ({progressUpdates.length})
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            {progressUpdates.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No progress updates yet.</p>
            ) : (
              progressUpdates.map((u) => (
                <div key={u.id} className="p-4 bg-white/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-gradient-to-br from-sky-400 to-cyan-500 text-white">
                            {u.user_name?.charAt(0) || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-sm">{u.user_name}</p>
                          <p className="text-xs text-gray-500">{format(new Date(u.created_at), "MMM dd, yyyy HH:mm")}</p>
                        </div>
                      </div>

                      <div className="mb-2">
                        <span className="font-semibold">Progress:</span> {u.progress}% 
                      </div>

                      {u.message && (
                        <p className="text-gray-700 whitespace-pre-wrap break-all leading-relaxed max-w-full">
                          {u.message}
                        </p>
                      )}

                      {u.commit_url && (
                        <div className="mt-2">
                          <a href={u.commit_url} target="_blank" rel="noreferrer" className="text-sky-600 underline">
                            View Commit / Demo
                          </a>
                        </div>
                      )}

                      {u.file_url && (
                        <div className="mt-2">
                          <a href={u.file_url} target="_blank" rel="noreferrer" className="text-sky-600 underline">
                            Download Attachment
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="text-sm text-gray-500 ml-4">
                      {/* Placeholder: you can put actions (edit/delete) here for author/owner */}
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* COMMENTS */}
        <Card className="glass border-white/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Comments ({comments.length})
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Comment Input */}
            <form onSubmit={handleCommentSubmit} className="flex gap-2">
              <Input placeholder="Add a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} />
              <Button type="submit" className="bg-gradient-to-r from-sky-500 to-cyan-500">
                Post
              </Button>
            </form>

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No comments yet.</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-4 bg-white/50 rounded-lg">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-gradient-to-br from-sky-400 to-cyan-500 text-white">
                        {comment.user_name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-sm">{comment.user_name}</p>
                        <span className="text-xs text-gray-500">{format(new Date(comment.created_at), "MMM dd, yyyy")}</span>
                      </div>
                      <p className="text-gray-700">{comment.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
